import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box } from '@mui/material'
import { refreshUserProfile, updateUser } from '@/api/user-manager'
import { routes } from '@/router'
import { UserProfile } from '@/types'
import { sessionButtonSx } from '../session-button'
import { SessionField, SessionOption, SessionScreen } from '../session-screen'
import { LANDING_CHANNELS } from './profile.constants'
import { flowProgress, previousProfileStep } from './profile-steps'
import { ProfileStepFrame } from './profile-step-frame'

function isKnownChannel(
  value: string,
): value is (typeof LANDING_CHANNELS)[number]['id'] {
  return LANDING_CHANNELS.some((item) => item.id === value)
}

function readChannel(user: UserProfile) {
  const channel = user.landing_channel
  if (!channel) return { selected: '', other: '' }
  if (isKnownChannel(channel) && channel !== 'other') {
    return { selected: channel, other: '' }
  }
  return { selected: 'other', other: channel === 'other' ? '' : channel }
}

export function ProfileOriginPage() {
  return (
    <ProfileStepFrame step="origin">
      {(user) => <OriginStep user={user} />}
    </ProfileStepFrame>
  )
}

function OriginStep({ user }: { user: UserProfile }) {
  const navigate = useNavigate()
  const initial = readChannel(user)
  const [selected, setSelected] = useState(initial.selected)
  const [other, setOther] = useState(initial.other)
  const [saving, setSaving] = useState(false)
  const previous = previousProfileStep('origin', user)

  const isValid =
    Boolean(selected) && (selected !== 'other' || other.trim().length > 1)

  async function finish() {
    if (!isValid || saving) return
    setSaving(true)

    try {
      const landingChannel = selected === 'other' ? other.trim() : selected
      await updateUser({ landing_channel: landingChannel })
      await refreshUserProfile()
      navigate(routes.home)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SessionScreen
      title="Onde você conheceu a Alume?"
      progress={flowProgress('origin', user)}
      onBack={() =>
        navigate(
          previous
            ? routes.session.profile[previous]
            : routes.session.profile.area,
        )
      }
      footer={
        <LoadingButton
          size="large"
          fullWidth
          loading={saving}
          disabled={!isValid}
          onClick={finish}
          sx={sessionButtonSx()}
        >
          Concluir
        </LoadingButton>
      }
    >
      <Box
        role="radiogroup"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
      >
        {LANDING_CHANNELS.map((item) => (
          <SessionOption
            key={item.id}
            selected={selected === item.id}
            onClick={() => setSelected(item.id)}
          >
            {item.label}
          </SessionOption>
        ))}
      </Box>
      {selected === 'other' && (
        <SessionField
          id="origin-other"
          label="Conte pra gente de onde conheceu a Alume"
          value={other}
          placeholder="Ex.: LinkedIn, IA, eventos…"
          onChange={(event) => setOther(event.target.value)}
          autoFocus
        />
      )}
    </SessionScreen>
  )
}
